import * as React from 'react';
import uniqBy from "lodash-es/uniqBy";
import { Column, Table } from "../../../Components/QueryTable";
import { MatchSortHeader } from "../../../Components/MatchSortHeader";
import { SortHeader } from "../../../Components/SortHeader";
import { DateMatchSortHeader } from "../../../Components/DateMatchSortHeader";
import { AnalysisAcceptabilityStatus } from "../../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { useQuery } from "../../../Components/Query";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { formatDateTime } from "../../../misc/components/Formatters/Formatters";
import { SvcAnalysisSet, SvcSession, WebPortalClient } from 'webportalclient';
import { SpirometryAnalysisSetTypeEnum } from '../../../misc/api/ApiSpirometryAnalysisSetType';
import { OverreadStatusHeader } from '../../../Components/OverreadStatusHeader';
import { HamburgerMenu } from "../../../misc/components/HamburgerMenu";
import { Authorization } from "../../../misc/components/Authorization";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from '../../../Core/SecurityContext';
import { Link } from "react-router-dom";
import DocumentTitle from '../../../misc/components/DocumentTitle';
import { useWebPortalApiClient } from '../../../misc/api/WebPortalTokenClient';

export interface SvcSessionsListTableProps {
}

export const SvcSessionsListTable = (props: SvcSessionsListTableProps) => {
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [technicianOptions, setTechnicianOptions] = React.useState([]);
    const { data, applyFilters, filters, refresh } = useQuery();
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [overreadStatuses, setOverreadStatuses] = React.useState([]);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);
    const { currentUserPermissions } = useSecurityContext();

    const hamburgerMenuPermissions = [
        PermissionEnum.AccessSingleSvcSession
    ]
    const hamburgerMenuDisabled = !currentUserPermissions.some(x=> hamburgerMenuPermissions.some(e=> e == x.Id));


    const FORMATTER_EMPTY = '-';

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.number,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.number,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getSvcSessions(undefined, `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getSvcSessions("name,technician", `visit/subject/site/studyId eq ${study.id}`, "name");

            const sessionOptions = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            const technicianOptions = uniqBy(sessions.value, o => o.technician).map(s => ({
                value: s.technician,
                label: s.technician,
            }));

            setSessionOptions(sessionOptions);
            setTechnicianOptions(technicianOptions);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getSvcAnalysisSets("analystUserId,analystUserName", `session/visit/subject/site/studyId eq ${study.id}`);

            const options = uniqBy(analysts.value, o => o.analystUserId).filter(o => o.analystUserId).map(s => ({
                value: s.analystUserId,
                label: s.analystUserName,
            }));

            setAnalysts(options);
        };

        const getOverreadStatuses = async () => {
            const overreadStatuses = await client.client.getOverreadStatuses();
            
            const options = uniqBy(overreadStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadStatuses(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalysts();
        getOverreadStatuses();
        getOverreadChallengeStatuses();

    }, [study.id]);


    const clearAllFilters = () => {
        applyFilters([]);
    }

    const fetchOverreaderName = (session: SvcSession): string => {
        if (!session)
            return FORMATTER_EMPTY;

        let overreaderAnalysisSet = filterAnalysisSetForOverread(session);

        if (overreaderAnalysisSet == null)
            return FORMATTER_EMPTY;

        return overreaderAnalysisSet.analystUserName || FORMATTER_EMPTY;
    }

    const filterAnalysisSetForOverread = (session: SvcSession): SvcAnalysisSet => {
        let analysisSets: SvcAnalysisSet[] = session.analysisSets;

        if (analysisSets == null)
            return null;

        return analysisSets.find(a => a.type == SpirometryAnalysisSetTypeEnum.Overread);

    }
    const formatOverreadChallengeStatus = (session: SvcSession): any => {
        if (!session)
            return FORMATTER_EMPTY;

        let overreaderAnalysisSet = filterAnalysisSetForOverread(session);

        return (overreaderAnalysisSet !== undefined ? <>
            <AnalysisAcceptabilityStatus value={overreaderAnalysisSet.status} /><br />
        </> : FORMATTER_EMPTY);
    }

    const GetOverreadAnalysisSetId = (session: SvcSession): string => {
        var overreadSet = session.analysisSets.find(as => as.type === SpirometryAnalysisSetTypeEnum.Overread);
        return overreadSet ? overreadSet.id : null;
    };

    const RenderHamburgerMenu = (
        session: SvcSession
    ) => {

        var overreadSetId = GetOverreadAnalysisSetId(session);

        return overreadSetId ? (
            <HamburgerMenu automationId={`options_${overreadSetId}`} disabled={hamburgerMenuDisabled}>
                {(
                    <Authorization
                        permissionId={PermissionEnum.AccessSingleSvcSession}
                        studyId={study.id}
                        currentPermissions={currentUserPermissions}>
                        <li>
                            <Link data-automationid={ `ViewLink_${overreadSetId}` } to={`/Svc/Analysis/${overreadSetId}/view`}>View</Link>
                        </li>
                    </Authorization>
                )}
            </HamburgerMenu> 
        ) : null;
    };

    return (
        <>
            <DocumentTitle title="SVC Sessions | Vitalograph Web Portal">
                <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">SVC Sessions</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button
                            className="btn btn-primary"
                            onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
                <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive" style={{ minHeight: "900px" }}>
                                <Table automationId="svcSessionsTable" data={data}>
                                    <Column
                                        field="visit"
                                        header={<MatchSortHeader
                                            title="Site ID"
                                            automationId="Site"
                                            field="visit/subject/site/number"
                                            options={siteOptions}
                                            sortField="visit/subject/site/number" />}
                                        formatter={visit => visit.subject.site.number} />
                                      <Column
                                        field="visit"
                                        header={<MatchSortHeader
                                            title="Subject ID"
                                            automationId="Subject"
                                            field="visit/subject/number"
                                            options={subjectOptions}
                                            sortField="visit/subject/number" />}
                                        formatter={visit => visit.subject.number} />

                                     <Column
                                        field="visit"
                                        header={<MatchSortHeader
                                            title="Visit"
                                            automationId="Visit"
                                            field="visit/name"
                                            options={visitOptions}
                                            sortField="visit/name" />}
                                        formatter={visit => visit.name} />

                                    <Column
                                        field=""
                                        header={<MatchSortHeader
                                            title="Session"
                                            automationId="Session"
                                            field="name"
                                            options={sessionOptions}
                                            sortField="name" />}
                                        formatter={session => session.name} />

                                    <Column
                                        field=""
                                        header={<DateMatchSortHeader
                                            title="Session Start"
                                            automationId="Occurred"
                                            field="occurred"
                                            sortField="occurred" />}
                                        formatter={session => formatDateTime(session.occurred)} />

                                    <Column
                                        field=""
                                        header={<SortHeader
                                            title="Tests"
                                            automationId="Tests"
                                            field="tests"
                                            />}
                                        formatter={session => session.tests.length} />

                                    <Column
                                        field=""
                                        header={<MatchSortHeader
                                            title="Technician"
                                            automationId="Technician"
                                            field="technician"
                                            options={technicianOptions}
                                            sortField="technician" />}
                                        formatter={session => session.technician} />

                                    <Column
                                        field=""
                                        header={<DateMatchSortHeader
                                            title="Received"
                                            automationId="Received"
                                            field="lastReceivedTime"
                                            sortField="lastReceivedTime" />}
                                        formatter={session => formatDateTime(session.lastReceivedTime)} />

                                    <Column
                                        field=""
                                            header={<OverreadStatusHeader
                                                automationId="OverreadStatus"
                                                orderField="overreadChallengeStatus"
                                                overreadStatuses={overreadStatuses}
                                                overreadChallengeStatuses={overreadChallengeStatuses}
                                                sortField="overreadChallengeStatus" />
                                        }
                                        formatter={session => formatOverreadChallengeStatus(session)} />
                                    <Column
                                        field=""
                                        header={<MatchSortHeader
                                            title="Overreader"
                                            automationId="Overreader"
                                            field="analystUserId"
                                            options={analysts}
                                            sortField="analystUserName" />}
                                        formatter={session => fetchOverreaderName(session)} />
                                    <Column 
                                        field=""
                                        formatter={session => RenderHamburgerMenu(session)}
                                    />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </DocumentTitle>
        </>
    );
}

export default SvcSessionsListTable;
